import type { ReturnRequestDetail, ReturnRequestFormValues } from '../../types/vipMp';
import { defaultFormValues, newLineItem } from './buildSubmitPayload';

function pick(row: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!row) return '';
  const lower = Object.fromEntries(
    Object.entries(row).map(([k, v]) => [k.toLowerCase(), v])
  );
  for (const k of keys) {
    const v = row[k] ?? lower[k.toLowerCase()];
    if (v != null && String(v).trim() !== '') return String(v);
  }
  return '';
}

function fmtDateForInput(iso: string | undefined): string {
  if (!iso) return '';
  const d = String(iso).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
}

/** Prefill wizard from GET /return-requests/:id (best-effort across column casings). */
export function mapDetailToFormValues(detail: ReturnRequestDetail): ReturnRequestFormValues {
  const h = (detail.header || {}) as Record<string, unknown>;
  const base = defaultFormValues();
  const lines = (detail.lineItems || []) as Record<string, unknown>[];
  const mappedLines =
    lines.length > 0
      ? lines.map((li, idx) => ({
          id: String(idx + 1),
          productDescription: pick(li, ['ProductDescription', 'productDescription']),
          adobeSku: pick(li, ['AdobeSku', 'adobeSku']),
          deployToId: pick(li, ['DeployToId', 'deployToId']),
          unitPrice: String(pick(li, ['UnitPrice', 'unitPrice']) || ''),
          quantity: String(pick(li, ['Quantity', 'quantity']) || '1'),
          currencyCode: pick(li, ['CurrencyCode', 'currencyCode', 'LineCurrencyCode', 'lineCurrencyCode', 'RequestCurrencyCode', 'requestCurrencyCode']) || 'USD'
        }))
      : [newLineItem('1')];

  const lockCurrency = mappedLines[0]?.currencyCode || 'USD';
  const normalizedLines = mappedLines.map((r, i) => ({
    ...r,
    currencyCode: i === 0 ? r.currencyCode : lockCurrency
  }));

  return {
    ...base,
    partnerName: pick(h, ['PartnerName', 'partnerName']) || base.partnerName,
    contactName: pick(h, ['SubmitterName', 'submitterName']) || base.contactName,
    contactEmail: pick(h, ['SubmitterEmail', 'submitterEmail']) || base.contactEmail,
    orderNumber: pick(h, ['AdobeSalesOrderNumber', 'adobeSalesOrderNumber']) || base.orderNumber,
    replacementOrder: pick(h, ['ReplacementOrderNumber', 'replacementOrderNumber']) || '',
    orderDate: fmtDateForInput(pick(h, ['OrderDate', 'orderDate'])),
    accountId: pick(h, ['CustomerMarketplaceAccountId', 'customerMarketplaceAccountId']) || '',
    customerName: pick(h, ['CustomerName', 'customerName']) || '',
    geoCode: pick(h, ['GeoCode', 'geoCode']) || '',
    returnTypeCode: pick(h, ['ReturnTypeCode', 'returnTypeCode']) || '',
    reasonCode: pick(h, ['ReasonCode', 'reasonCode']) || '',
    timeframeCode: pick(h, ['TimeframeCode', 'timeframeCode']) || '',
    managerName: pick(h, ['ManagerName', 'managerName', 'AdobeAccountManagerName']) || '',
    managerEmail: pick(h, ['ManagerEmail', 'managerEmail', 'AdobeAccountManagerEmail']) || '',
    partnerComments: pick(h, ['PartnerComments', 'partnerComments']) || '',
    lineItems: normalizedLines,
    feeAcknowledged: false,
    accuracyConfirmed: false
  };
}
